import bindAllMethods from '../../utils/bindAllMethods';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import { TenantStrategyEnum } from '../tenantHandler/strategy/strategy';
import ISessionService, {
  RefreshParameterType,
  SignInOptionsType,
  SignInResponseType
} from './ISessionService';
import { GenerateAuthenticationUrlParams } from './loginService';
import type {
  AuthProvidersPropsType,
  SetServiceDependencies,
  ShellFlagsInputType
} from '../../infra/commonInitializer/types';
import IRefreshTokenService from './refreshTokenService/IRefreshTokenService';
import IExchangeTokenService from './exchangeTokenService/IExchangeTokenService';
import { inject, singleton } from 'tsyringe';
import ExchangeTokenServiceNative from './exchangeTokenService/ExchangeTokenServiceNative';
import { JWebEnums, JWebErrorHandler, ValueStoreFacade } from '../JWeb';
import {
  AuthContextEnum,
  AuthTokenService,
  IAuthTokenService
} from '../authTokenService';
import {
  TokenProviderOptions,
  ValueStoreGetResult,
  ValueStoreGetSuccessResult
} from '../JWeb/types';
import {
  TokenLifetimeRequirementType,
  TokenType
} from '../../services/JWeb/JWebEnums';
import { getUserInteractionEntryPointBasedOnParams } from '../mfeRouterService/operations/NewLoginPathOperation/utils/getUserInteractionEntryPoint';
import { AccessToken, AuthPluginError } from '../../services/JWeb/types';
import * as JWebEnumsImport from '../JWeb/JWebEnums';
import IApplicationService from '../applicationService/IApplicationService';
import type { LoginInputType } from './types';
import BaseSessionService from './BaseSessionService';
import AuthenticationProviderEnum from '../../config/authenticationProviderEnum';
import getConfigIdBasedOnStack from './utils/getConfigIdBasedOnStack';
import { JWEB_SIGNIN_REQUEST_ID } from './constants';
import { AuthPluginFacade } from '../JWeb/facades/AuthPluginFacade';

@singleton()
class SessionServiceNative
  extends BaseSessionService
  implements ISessionService
{
  private _refreshTokenServiceNative: IRefreshTokenService;
  private _exchangeTokenServiceNative: IExchangeTokenService;
  private _legacySessionDataValue = {
    isLoggedIn: false,
    idToken: ''
  };
  private _applicationService: IApplicationService;
  private _loginProps: LoginInputType;
  private _authProviderProps: AuthProvidersPropsType;

  private valueStoreadditionalAuthorizationParametersKey =
    'clientOSAdditionalAuthorizationParams';

  private _forceDefaultAdditionalAuthorizationParams: boolean;

  constructor(
    @inject(AuthTokenService)
    authTokenService: IAuthTokenService,
    @inject(ExchangeTokenServiceNative)
    exchangeTokenServiceNative: IExchangeTokenService,
    @inject('IRefreshTokenService')
    refreshTokenServiceNative: IRefreshTokenService,
    @inject('ApplicationService') applicationService: IApplicationService,
    @inject('LoginProps')
    loginProps: LoginInputType,
    @inject('AuthProviderProps') authProviderProps: AuthProvidersPropsType,
    @inject('ShellFlagsProps') shellFlagsProps: ShellFlagsInputType
  ) {
    super({ authTokenService, isNative: true });
    this._exchangeTokenServiceNative = exchangeTokenServiceNative;
    this._refreshTokenServiceNative = refreshTokenServiceNative;
    this._applicationService = applicationService;
    this._loginProps = loginProps;
    this._authProviderProps = authProviderProps;
    this._forceDefaultAdditionalAuthorizationParams =
      shellFlagsProps?.forceDefaultAdditionalAuthorizationParams;

    this.setRefreshMethods({
      init: this._updateInitialState.bind(this),
      refresh: this._updateInitialState.bind(this)
    });

    bindAllMethods(this);
  }

  async signIn(options: SignInOptionsType): Promise<SignInResponseType> {
    const { target } = options || {};

    let parsedValueStoreadditionalAuthorizationParametersKey = {};
    try {
      const valueStoreadditionalAuthorizationParameters =
        (await ValueStoreFacade.get({
          keys: [
            this._loginProps?.overrideClientOSAdditionalAuthorizationParams ||
              this.valueStoreadditionalAuthorizationParametersKey
          ]
        })) as ValueStoreGetResult;

      if (valueStoreadditionalAuthorizationParameters) {
        parsedValueStoreadditionalAuthorizationParametersKey = JSON.parse(
          (
            valueStoreadditionalAuthorizationParameters.result as unknown as ValueStoreGetSuccessResult
          )?.[0].value
        );
      }
    } catch (error) {
      internalLogger.error(
        'Error getting additionalAuthorizationParameters from ValueStore',
        error
      );
    }

    const additionalAuthorizationParameters = {
      ...this._loginProps?.additionalAuthorizationParameters,
      ...parsedValueStoreadditionalAuthorizationParametersKey
    };

    if (target) additionalAuthorizationParameters['target'] = target;

    const tokenProviderOptions: TokenProviderOptions = {
      tokenType: JWebEnums.TokenType.user,
      allowNetworkAccess: true,
      allowUserInteraction: true,
      requireFreshToken: true,
      showAccountCreationLink: true,
      skipTokenRefresh: false,
      userInteractionEntryPoint: JWebEnums.UserInteractionEntryPoint.signIn,
      // @ts-ignore
      requestId: JWEB_SIGNIN_REQUEST_ID, // requestId is not in the type definition for this JWeb Core version yet.
      additionalAuthorizationParameters
    };

    const response = await AuthPluginFacade.getToken({
      tokenProviderOptions
    });

    if ((response as AuthPluginError).error) {
      return {
        success: false,
        error: {
          message: (response as AuthPluginError).error.code
        }
      };
    }

    return {
      success: true
    };
  }

  public setDependencies({ services, ...rest }: SetServiceDependencies): void {
    super.setDependencies({ services, ...rest });

    bindAllMethods(this);
  }

  public async init(): Promise<void> {
    /* istanbul ignore next */
    internalLogger.log('Initializing session service native.');

    await this._updateInitialState();
    await this._setStratusAccessTokenFromNative();

    return Promise.resolve();
  }

  public getLoginCooldown(): number {
    return this._criticalScopeHandler.getLoginCooldown();
  }

  /** This should be used to ensure that critical scopes is invalid and the login is not in cooldown. */
  public goForceLogin(waitCallback = () => {}): void {
    // Only if the isCriticalScopesValid is false and the getLoginCooldown is false, we redirect to login.
    if (this._criticalScopeHandler.ensureCriticalScopes(waitCallback)) {
      this._loginService.redirectToLogin();
    }
  }

  public async refresh(
    refreshParameterType?: RefreshParameterType
  ): Promise<void> {
    if (!this.isLoggedIn()) {
      return;
    }
    // if (this._criticalScopeHandler.getLoginCooldown()) {
    //   /* istanbul ignore next */
    //   internalLogger.debug('Login cooldown is active, skipping refresh token');
    //   return;
    // }
    return this._refreshTokenServiceNative.refresh(refreshParameterType);
  }

  private _getTokenProviderOptions(
    tokenProviderOptions: TokenProviderOptions
  ): TokenProviderOptions {
    const options = { ...tokenProviderOptions };

    if (options.requireFreshToken) {
      options.tokenLifetimeRequirements = [
        {
          type: TokenLifetimeRequirementType.preferredMaximumSecondsSinceIssued,
          timeInterval: 0
        }
      ];
    }

    if (options.requireFreshToken && options.skipTokenRefresh) {
      options.scopesRequested = ['openid'];
    }

    return options;
  }

  private async _exchangeNativeForceLoginToken(): Promise<
    AuthPluginError | AccessToken
  > {
    const exchangeResult = await (
      this._exchangeTokenServiceNative as ExchangeTokenServiceNative
    ).exchangeJWeb(
      null,
      this._getTokenProviderOptions({
        tokenType: TokenType.user,
        allowUserInteraction: true,
        skipTokenRefresh: true,
        requireFreshToken: true
      })
    );

    return exchangeResult;
  }

  private old_getAdditionalParamsForJwebGetToken() {
    /* istanbul ignore next */
    internalLogger.log('Using default additionalAuthorizationParameters');

    return {
      additionalAuthorizationParameters: {
        config_id: getConfigIdBasedOnStack(
          this._applicationService.getPortalStack()
        )
      }
    };
  }

  private new_getAdditionalParamsForJwebGetToken() {
    const isCoptor =
      this?._authProviderProps?.authenticationProvider ===
      AuthenticationProviderEnum.coptor;

    if (isCoptor) {
      const manifestAdditionalAuthParams = {
        additionalAuthorizationParameters:
          this._loginProps?.additionalAuthorizationParameters
      };

      /* istanbul ignore next */
      internalLogger.log(
        'Using additionalAuthorizationParameters from manifest: ',
        manifestAdditionalAuthParams
      );

      return manifestAdditionalAuthParams;
    }

    /* istanbul ignore next */
    internalLogger.log('Using empty additionalAuthorizationParameters');
    return {};
  }

  private getAdditionalParamsForJwebGetToken() {
    // JWEB has a bug when we do not pass the additionalAuthorizationParameters. We created a workaround using shellFlag to pass a default parameter.
    if (this._forceDefaultAdditionalAuthorizationParams) {
      return this.old_getAdditionalParamsForJwebGetToken();
    }
    return this.new_getAdditionalParamsForJwebGetToken();
  }

  public async redirectToIdentityProvider(): Promise<any> {
    const entryPoint = getUserInteractionEntryPointBasedOnParams() || 'signIn';

    // If the user is not logged in.
    if (!this.isLoggedIn()) {
      // ? This is the login of the user in login flow. We dont need to setToken here, because we can get affter of the /loggedin flow.
      const exchangeResult = await (
        this._exchangeTokenServiceNative as ExchangeTokenServiceNative
      ).exchangeJWeb(
        null,
        this._getTokenProviderOptions({
          userInteractionEntryPoint:
            JWebEnumsImport.UserInteractionEntryPoint[entryPoint],
          tokenType: TokenType.user,
          allowUserInteraction: true,
          ...this.getAdditionalParamsForJwebGetToken()
        })
      );

      return exchangeResult;
    }
    // So, the user is logged in, we should apply the force Login.
    return this._exchangeNativeForceLoginToken();
  }

  public async exchangeToken(
    tenantId: string,
    authContext: AuthContextEnum,
    tenantStrategy: TenantStrategyEnum
  ): Promise<void> {
    return this._exchangeTokenServiceNative.exchange({
      tenantId,
      authContext,
      tenantStrategy
    });
  }

  public async clearSession(): Promise<void> {
    this._legacySessionDataValue.isLoggedIn = false;
    this._legacySessionDataValue.idToken = '';
    await AuthPluginFacade.logout().then((v) => JWebErrorHandler(v));
  }

  public async logout(): Promise<void> {
    await this.clearSession();
    window.location.reload();
  }

  public async generateAuthenticationUrl(
    options: GenerateAuthenticationUrlParams
  ): Promise<string> {
    return this._loginService.generateAuthenticationUrl(options);
  }

  public isLoggedIn(): boolean {
    return !!this._legacySessionDataValue?.isLoggedIn;
  }

  public getIdToken(): string {
    return this._legacySessionDataValue?.idToken;
  }

  private async _updateInitialState(): Promise<void> {
    try {
      const isLoggedInData = (await AuthPluginFacade.isLoggedIn()) as {
        value: boolean;
      };
      const idTokenData = await AuthPluginFacade.getToken({
        tokenProviderOptions: {
          tokenType: TokenType.user
        }
      });

      this._legacySessionDataValue.isLoggedIn = isLoggedInData.value;
      this._legacySessionDataValue.idToken = (
        idTokenData as AccessToken
      )?.account?.idToken;
    } catch (e) {
      console.error('Failed from get Auth data from JWeb.');
      console.error(e);
      this._legacySessionDataValue.isLoggedIn = false;
    }
  }

  private async _setStratusAccessTokenFromNative(): Promise<void> {
    try {
      if (this.isLoggedIn()) {
        return this._refreshTokenServiceNative.refresh();
      }
    } catch (error) {
      /* istanbul ignore next */
      internalLogger.error('Error on refresh token');
      console.error(error);
      return;
    }
  }
}

export default SessionServiceNative;
